import { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '@/components/DataTable/DataTable';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import SftpConnectionFormDialog from '@/views/Organizations/SftpConnections/SftpConnectionFormDialog';
import { formatTimestamp } from '@/utils/date/date';
import useFormValidation from '@/hooks/useFormValidation';

const SftpConnectionsTab = ({ organizationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const handleDelete = async (connectionId, aliasName) => {
    const confirmMessage = t('sftpConnections.confirmDelete', {
      name: aliasName,
    });

    requestConfirmation(confirmMessage, async () => {
      setLoading(true);
      try {
        await SftpConnectionsApi.deleteSftpConnection(
          organizationId,
          connectionId,
        );
        toast.success(t('sftpConnections.deleteSuccess'));
        dataTableRef.current?.refresh();
      } catch (err) {
        console.error('Error deleting SFTP connection:', err);
        toast.error(
          err.response?.data?.message || 'Failed to delete connection',
        );
      } finally {
        setLoading(false);
      }
    });
  };

  const handleCreate = () => {
    clearAllErrors();
    setDialogOpen(true);
  };

  const handleEdit = (connectionId) => {
    navigate(
      `/admin/organizations/${organizationId}/sftp-connections/${connectionId}`,
    );
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    clearAllErrors();
  };

  const handleSave = async (formData) => {
    clearAllErrors();
    setLoading(true);
    try {
      const response = await SftpConnectionsApi.createSftpConnection(
        organizationId,
        formData,
      );
      toast.success(t('sftpConnections.createSuccess'));
      navigate(
        `/admin/organizations/${organizationId}/sftp-connections/${response.data.data.id}`,
      );
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('sftpConnections.search'),
        filterType: 'search',
        placeholder: t('sftpConnections.searchPlaceholder'),
        debounce: true,
        debounceDelay: 300,
      },
      {
        key: 'type',
        label: t('sftpConnections.table.type'),
        filterType: 'select',
        options: [
          { value: '', label: t('sftpConnections.filters.allTypes') },
          {
            value: 'facility_users',
            label: t('sftpConnections.filters.facilityUsers'),
          },
          {
            value: 'facility_locations',
            label: t('sftpConnections.filters.facilityLocations'),
          },
        ],
      },
    ],
    [t],
  );

  const columns = [
    {
      key: 'alias_name',
      label: t('sftpConnections.table.aliasName'),
      sortable: true,
    },
    {
      key: 'host',
      label: t('sftpConnections.table.host'),
      sortable: true,
    },
    {
      key: 'port',
      label: t('sftpConnections.table.port'),
      render: (value) => value || 22,
    },
    {
      key: 'username',
      label: t('sftpConnections.table.username'),
      sortable: true,
    },
    {
      key: 'type',
      label: t('sftpConnections.table.type'),
      render: (value) => {
        if (!value) return '-';
        if (value === 'facility_users') {
          return t('sftpConnections.filters.facilityUsers');
        }
        if (value === 'facility_locations') {
          return t('sftpConnections.filters.facilityLocations');
        }
        return value;
      },
    },
    {
      key: 'folders',
      label: t('sftpConnections.table.folders'),
      render: (value) => {
        if (!value || value.length === 0) return '-';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {value.slice(0, 2).map((folder, index) => (
              <Chip key={index} label={folder} size='small' />
            ))}
            {value.length > 2 && (
              <Chip
                label={`+${value.length - 2}`}
                size='small'
                variant='outlined'
              />
            )}
          </Box>
        );
      },
    },
    {
      key: 'created_at',
      label: t('sftpConnections.table.createdAt'),
      sortable: true,
      render: (value) => formatTimestamp(value),
    },
    {
      key: 'updated_at',
      label: t('sftpConnections.table.updatedAt'),
      sortable: true,
      render: (value) => formatTimestamp(value),
    },
    {
      key: 'actions',
      label: t('sftpConnections.table.actions'),
      sortable: false,
      align: 'right',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={t('sftpConnections.editTooltip')}>
            <IconButton
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.id);
              }}
              disabled={loading}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('sftpConnections.deleteTooltip')}>
            <IconButton
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id, row.alias_name);
              }}
              disabled={loading}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pt: 2 }}>
      <DataTable
        ref={dataTableRef}
        columns={columns}
        endpoint={`/organizations/${organizationId}/sftp-connections`}
        initialSort={{ key: 'created_at', order: 'desc' }}
        pageSize={50}
        autoFetch={true}
        enableColumnPicker={true}
        enableSorting={true}
        enableFilters={true}
        columnFilters={columnFilters}
        externalLoading={loading}
        renderToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<AddIcon />}
              onClick={handleCreate}>
              {t('sftpConnections.createButton')}
            </Button>
          </Box>
        )}
        renderEmptyState={() => (
          <TableRow>
            <TableCell colSpan={columns.length} align='center' sx={{ py: 8 }}>
              <Typography variant='body1' color='text.secondary'>
                {t('sftpConnections.noData')}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      />

      <SftpConnectionFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        organizationId={organizationId}
        onSave={handleSave}
        loading={loading}
        fieldErrors={fieldErrors}
        generalErrors={generalErrors}
      />
    </Box>
  );
};

export default SftpConnectionsTab;
